import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import { API_PATH } from "@/constants/config";
import { request } from "@/helper/http";
import { invalidatesOn } from "@/helper/invalidatesOn";
import { toCamelCase } from "@/helper/toCamelCase";
import { toSnakeCase } from "@/helper/toSnakeCase";
import { type IErrorResponse } from "@/types/error";
import { type IIpWhitelistSetting, type IIpWhitelistSettingServer } from "@/types/ipWhitelist";

const getIpWhitelist = (): Promise<IIpWhitelistSettingServer> => {
  return request({
    method: "GET",
    url: `${API_PATH.SYSTEM_SETTINGS_IP_WHITELIST}`,
  });
};

const updateIpWhitelist = (payload: Partial<IIpWhitelistSettingServer>): Promise<IIpWhitelistSettingServer> => {
  return request({
    method: "PATCH",
    url: `${API_PATH.SYSTEM_SETTINGS_IP_WHITELIST}`,
    data: payload,
  });
};

export const apiIpWhitelist = createApi({
  reducerPath: "apiIpWhitelist",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["IpWhitelist"],
  endpoints: (builder) => ({
    getIpWhitelist: builder.query({
      queryFn: async (): Promise<{ data: IIpWhitelistSetting } | { error: unknown }> => {
        try {
          const serverData = await getIpWhitelist();
          const transformedData = toCamelCase<IIpWhitelistSetting>({ object: serverData });
          return { data: transformedData };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["IpWhitelist"],
    }),
    updateIpWhitelist: builder.mutation({
      queryFn: async (payload: Partial<IIpWhitelistSetting>) => {
        try {
          const serverPayload = toSnakeCase<Partial<IIpWhitelistSettingServer>>({ object: payload });
          const data = await updateIpWhitelist(serverPayload);
          const transformedData = toCamelCase<IIpWhitelistSetting>({ object: data });
          return { data: transformedData };
        } catch (error) {
          return { error: error as IErrorResponse };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["IpWhitelist"], error: [] }),
    }),
  }),
});

export const { useGetIpWhitelistQuery, useUpdateIpWhitelistMutation } = apiIpWhitelist;
